
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Campaign } from "@/types/campaigns";

export const useReactivateCampaign = (userId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (campaign: Campaign): Promise<Campaign> => {
      console.log('🔄 Reactivating campaign:', campaign.id, 'Current status:', campaign.status);

      // Validar se as datas ainda são válidas
      const today = new Date();
      const endDate = new Date(campaign.end_date);
      
      if (endDate < today) {
        throw new Error('Não é possível reativar uma campanha com data de término no passado. Edite as datas primeiro.');
      }

      // Determinar o novo status baseado nas datas
      const startDate = new Date(campaign.start_date);
      const newStatus = startDate <= today ? 'active' : 'scheduled';

      console.log('📅 Date validation - Start:', startDate.toISOString(), 'End:', endDate.toISOString(), 'Today:', today.toISOString());
      console.log('🎯 New status will be:', newStatus, 'Previous status:', campaign.status);

      // PRIMEIRO: Buscar configuração de webhook ANTES de atualizar a campanha
      let webhookConfig = null;
      try {
        const { data: config } = await supabase
          .from('webhook_configurations')
          .select('*')
          .eq('user_id', userId)
          .eq('is_active', true)
          .single();
        webhookConfig = config;
        console.log('🔧 Webhook config found:', !!webhookConfig);
      } catch (error) {
        console.warn('⚠️ No webhook configuration found or error fetching:', error);
      }

      // SEGUNDO: Atualizar o status da campanha diretamente
      const { data: updatedCampaign, error } = await supabase
        .from('campaigns')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', campaign.id)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating campaign:', error);
        throw error;
      }

      console.log('✅ Campaign updated successfully');

      // TERCEIRO: Se a campanha ficou ativa, aplicar as regras
      if (newStatus === 'active') {
        console.log('🛠️ Applying campaign rules for active campaign...');
        try {
          const { error: rulesError } = await supabase.rpc('apply_campaign_rules', {
            campaign_id_param: campaign.id
          });
          
          if (rulesError) {
            console.warn('⚠️ Error applying campaign rules:', rulesError);
          } else {
            console.log('✅ Campaign rules applied successfully');
          }
        } catch (rulesError) {
          console.warn('⚠️ Exception applying campaign rules:', rulesError);
        }

        // QUARTO: CHAMAR WEBHOOK DIRETAMENTE se tiver configuração
        if (webhookConfig) {
          try {
            console.log('🚀 CALLING WEBHOOK DIRECTLY - NO DATABASE LOGS!');
            
            // Preparar payload do webhook
            const webhookPayload = {
              event: 'campaign.activated',
              timestamp: new Date().toISOString(),
              data: {
                campaign_id: campaign.id,
                campaign_name: campaign.name,
                new_status: newStatus,
                user_id: userId,
                activated_at: new Date().toISOString(),
                event_type: 'activation'
              }
            };

            console.log('📤 Sending webhook payload:', webhookPayload);
            console.log('🎯 Webhook URL:', webhookConfig.webhook_url);

            // Chamar webhook diretamente via fetch
            const response = await fetch(webhookConfig.webhook_url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'User-Agent': 'Supabase-Webhook/1.0-Direct'
              },
              body: JSON.stringify(webhookPayload)
            });

            const responseText = await response.text();
            
            if (response.ok) {
              console.log('✅ Webhook called successfully:', response.status, responseText);
              toast.success(`Campanha reativada e webhook enviado com sucesso!`);
            } else {
              console.error('❌ Webhook failed:', response.status, responseText);
              toast.success("Campanha reativada com sucesso (webhook falhou)");
            }
          } catch (error) {
            console.error('❌ Failed to call webhook directly:', error);
            toast.success("Campanha reativada com sucesso (erro no webhook)");
          }
        } else {
          console.log('ℹ️ No webhook configuration, skipping webhook call');
          toast.success("Campanha reativada com sucesso");
        }
      } else {
        console.log('📅 Campaign scheduled for future, no webhook processing needed');
        toast.success(`Campanha reativada com sucesso! Status: agendada`);
      }

      // Retornar dados atualizados da campanha com tipos corretos
      return {
        ...updatedCampaign,
        product: updatedCampaign.product as "cashback" | "referral",
        status: updatedCampaign.status as "draft" | "scheduled" | "active" | "completed" | "cancelled"
      } as Campaign;
    },
    onSuccess: (updatedCampaign) => {
      console.log('🎉 Reactivation successful! Campaign is now:', updatedCampaign.status);
      queryClient.invalidateQueries({ queryKey: ['campaigns', userId] });
    },
    onError: (error: any) => {
      console.error('💥 Failed to reactivate campaign:', error);
      toast.error(`Erro ao reativar campanha: ${error.message}`);
    },
  });
};
